import AsyncAutocompleteWithQuery, {
  AsyncAutocompleteWithQueryProps,
} from "components/AsyncAutocompleteWithQuery";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  BaseFilter,
  ListResponseOfLearningLevel,
} from "services/KnowfullyAdminClient";
import { IdValue } from "types/common-types";

type SelectLearningLevelProps = {
  value?: string | null;
  onChange: (id: string | null) => void;
  onDirty?: () => void;
  onClean?: () => void;
} & Omit<
  AsyncAutocompleteWithQueryProps<IdValue>,
  "fetchData" | "failMessage" | "labelField" | "idField" | "value" | "onChange"
>;

const SelectLearningLevel = ({
  value: learningLevelId,
  onChange,
  onDirty,
  onClean,
  ...rest
}: SelectLearningLevelProps) => {
  const { formatMessage: t } = useIntl();
  const { skuService } = useGlobalContext();
  const queryClient = useQueryClient();
  const [learningLevel, setLearningLevel] = useState<IdValue | null>(null);

  // Load the initial value if we're passed a learningLevelId
  const { isLoading, data } = useQuery<ListResponseOfLearningLevel>(
    ["learningLevelById", learningLevelId],
    () =>
      skuService.getLearningLevels(
        new AdminListRequestOfBaseFilter({ sortField: "value" })
      ),
    {
      enabled:
        !!learningLevelId &&
        (!learningLevel || learningLevel.id !== learningLevelId),
    }
  );

  useEffect(() => {
    // Once the initial value loads, update the value (if is hasn't been set by a search)
    if (data?.data?.[0]) {
      setLearningLevel({
        id: data!.data![0].id,
        value: data!.data![0].value ?? "?",
      });
    }
  }, [data, learningLevel]);

  const fetchLearningLevels = async (searchTerm: string) => {
    const result = await queryClient.fetchQuery<ListResponseOfLearningLevel>(
      ["learningLevels", searchTerm],
      () =>
        skuService.getLearningLevels(
          new AdminListRequestOfBaseFilter({
            pageNumber: 0,
            pageSize: 20,
            parameters: BaseFilter.fromJS({
              searchText: searchTerm === "" ? undefined : searchTerm,
            }),
            sortField: "value",
          })
        )
    );
    // We need to massage these results into the standard format

    const levels = {
      ...result,
      data: (result.data ?? []).map(({ id, value }) => ({
        id,
        value: value ?? "?",
      })),
    };

    return { result: levels, forSearchTerm: searchTerm };
  };

  const onChangeLearningLevel = (learningLevel: IdValue | null) => {
    setLearningLevel(learningLevel);
    onChange(learningLevel?.id ?? null);
    if (learningLevel !== null) {
      onDirty && onDirty();
    } else {
      onClean && onClean();
    }
  };

  return (
    <AsyncAutocompleteWithQuery
      {...rest}
      value={learningLevel}
      isLoadingInitialValue={isLoading}
      labelField="value"
      idField="id"
      fetchData={fetchLearningLevels}
      failMessage={t({ id: "selectProductFetchFailed" })}
      onChange={onChangeLearningLevel}
    />
  );
};

export default SelectLearningLevel;
